import { Link } from "react-router-dom";
import { useState, useEffect } from "react";
import {
  ArrowRight, Mic, Sparkles, BookOpen, ShieldCheck, Volume2, Image as ImageIcon,
  Search, Lock, Check, Sunrise, Moon, Zap,
  Heart, Waves, Star,
  // Connector-section icons
  MessageSquare, CalendarDays, CloudSun, MapPin, Link2, Radio, Mail, Users,
  // Hero-feature icons
  Handshake, FileText, Signal,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import HeroChat from "@/components/HeroChat";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Nav, Footer } from "@/components/landing";

/* ---------- Small helpers ---------- */
const Kicker = ({ children, testid }) => (
  <div data-testid={testid} className="text-[11px] uppercase tracking-[0.24em] font-semibold text-[var(--rammy-cyan-dark)]">
    {children}
  </div>
);
const H2 = ({ children, className = "" }) => (
  <h2 className={`font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[var(--rammy-text)] ${className}`}>
    {children}
  </h2>
);
const Lead = ({ children, className = "" }) => (
  <p className={`text-lg leading-relaxed text-[var(--rammy-text-sec)] ${className}`}>{children}</p>
);

/* Warm card — soft border, generous padding, subtle lift on hover. */
const Card = ({ children, className = "", testid }) => (
  <div
    data-testid={testid}
    className={`rounded-3xl bg-[var(--rm-surface)] border border-black/[0.06] p-8 shadow-[0_8px_30px_rgba(17,19,24,0.03)]
      transition-[transform,box-shadow] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(17,19,24,0.06)] ${className}`}
  >
    {children}
  </div>
);
const FeatureCard = ({ icon: Icon, title, children, className = "", testid }) => (
  <Card className={className} testid={testid}>
    <div className="w-11 h-11 rounded-2xl bg-[var(--rammy-cyan)]/15 flex items-center justify-center text-[var(--rammy-cyan-dark)] mb-5">
      <Icon size={18} strokeWidth={1.8}/>
    </div>
    <h3 className="font-display text-xl leading-snug mb-2">{title}</h3>
    <p className="text-[15px] leading-relaxed text-[var(--rammy-text-sec)]">{children}</p>
  </Card>
);

/* ---------- Sections ---------- */

// ─────────────────────────────────────────────────────────────────────────────
// Section: what Rammy is plugged into. Two honest groups — accounts a person
// actually connects (OAuth / token, revocable) and live sources Rammy checks
// on their behalf (nothing to connect, no account). No counts: a number turns
// this into a scoreboard, and the point here is capability, not coverage.
// Source of truth for what's live: PROVIDER_CATALOG in integrations_router.py.
// ─────────────────────────────────────────────────────────────────────────────
const CONNECTED_ACCOUNTS = [
  {
    id: "calendar",
    icon: CalendarDays,
    name: "Google Calendar",
    line: "Rammy reads your day and can book, move or cancel real time in it — with an hour to undo anything.",
  },
  {
    id: "contacts",
    icon: Users,
    name: "Google Contacts",
    line: "Who's who: people, the companies they work at, the roles they hold. A promise gets a face.",
  },
  {
    id: "gmail",
    icon: Mail,
    name: "Gmail",
    line: "The three emails that matter, and drafts you approve with a tap before anything sends.",
  },
  {
    id: "telegram",
    icon: MessageSquare,
    name: "Telegram",
    line: "Briefings and nudges arrive in a thread you already check, not one more inbox to remember.",
  },
];

const LIVE_SOURCES = [
  {
    id: "weather",
    icon: CloudSun,
    name: "Weather",
    line: "Rammy checks the sky where you are before it suggests the walk.",
  },
  {
    id: "places",
    icon: MapPin,
    name: "Maps & places",
    line: "\"Near me\", \"open now\", \"how far\" — answered from live place data.",
  },
];

function ConnectorGroup({ title, note, items, icon: GroupIcon, testid }) {
  return (
    <div
      className="rounded-3xl bg-[var(--rm-surface)] border border-black/[0.06] p-6 md:p-8 shadow-[0_8px_30px_rgba(17,19,24,0.03)]"
      data-testid={testid}
    >
      <div className="flex items-start gap-4">
        <span className="w-11 h-11 rounded-2xl bg-[var(--rammy-cyan)]/15 flex items-center justify-center text-[var(--rammy-cyan-dark)] shrink-0">
          <GroupIcon size={18} strokeWidth={1.8}/>
        </span>
        <div className="min-w-0">
          <div className="font-display text-lg leading-snug">{title}</div>
          <div className="text-sm text-[var(--rammy-text-sec)] leading-relaxed mt-0.5">{note}</div>
        </div>
      </div>

      <ul className="mt-7 space-y-5">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <li key={it.id} className="flex items-start gap-3.5" data-testid={`connector-item-${it.id}`}>
              <span className="mt-0.5 text-[var(--rammy-cyan-dark)] shrink-0">
                <Icon size={16} strokeWidth={1.9}/>
              </span>
              <div className="min-w-0">
                <div className="text-[15px] font-medium text-[var(--rammy-text)] flex items-center gap-2">
                  {it.name}
                  <span className="w-1.5 h-1.5 rounded-full bg-[var(--rammy-cyan-dark)]" aria-hidden="true"/>
                </div>
                <div className="text-sm leading-relaxed text-[var(--rammy-text-sec)] mt-0.5">{it.line}</div>
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function SectionConnectors() {
  return (
    <section id="connections" className="scroll-mt-24 py-20 md:py-24 border-t border-[var(--rammy-border)]/50" data-testid="section-connectors">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-3xl">
          <Kicker testid="kicker-connections">Connected to your day</Kicker>
          <H2 className="mt-3">
            Rammy can move your meetings, reach your people, and{" "}
            <span className="text-[var(--rammy-cyan-dark)]">answer where and&nbsp;when.</span>
          </H2>
          <Lead className="mt-5">
            Nothing here is a roadmap tile. Some of it is an account you connect and can
            revoke in one tap; the rest Rammy simply checks for you, with nothing to set up.
            We tell you which is which.
          </Lead>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-2 items-start">
          <ConnectorGroup
            testid="connector-group-accounts"
            icon={Link2}
            title="Accounts you connect"
            note="Your login, your permission, revocable any time."
            items={CONNECTED_ACCOUNTS}
          />
          <ConnectorGroup
            testid="connector-group-sources"
            icon={Radio}
            title="Live sources Rammy checks for you"
            note="No account, nothing to connect — Rammy just looks it up."
            items={LIVE_SOURCES}
          />
        </div>

        <div className="mt-10 flex items-center justify-center gap-4 text-sm text-[var(--rammy-text-sec)] flex-wrap">
          <span className="inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 rounded-full bg-[var(--rammy-cyan-dark)]" aria-hidden="true"/>
            <span><span className="font-medium text-[var(--rammy-text)]">Working today</span> — nothing on this page is a promise</span>
          </span>
          <span className="hidden sm:inline text-[var(--rammy-text-muted)]">·</span>
          <span className="inline-flex items-center gap-1.5"><ShieldCheck size={14} className="text-[var(--rammy-cyan-dark)]"/> Read-only by default</span>
          <span className="hidden sm:inline text-[var(--rammy-text-muted)]">·</span>
          <span className="inline-flex items-center gap-1.5"><Check size={14} className="text-[var(--rammy-cyan-dark)]"/> Every action logged, always yours to revoke</span>
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Section: the nine things a chat window can't do. Each card leads with a real
// spoken moment, then names the feature, so readers feel the value before they
// read the label.
//
// 🔴 HONESTY RULE (rewritten 20 Jun 2026): every card here must map to a
// capability that ships TODAY. This section previously advertised five
// features that did not exist in the codebase at all — Energy-Aware Planning
// (no health data anywhere), Relationship Radar, Real-world traffic nudges,
// Mood Modes and Meeting Brief — under a "Signature" badge. A homepage that
// over-promises converts a visitor once and loses them forever. If you want to
// add a card, grep the backend for the endpoint first.
// Guarded by `backend/tests/test_landing_copy_honesty.py`.
// ─────────────────────────────────────────────────────────────────────────────
const HERO_FEATURES = [
  {
    id: "promise-tracker",
    icon: Handshake,
    name: "Promises you actually keep",
    scene: "\"I'll send this tomorrow\" — caught.",
    line: "Rammy spots the promise buried in your sent mail, asks if it's real, then carries it until it's done — synced to your tasks, in both directions: what you owe, and what you're waiting on.",
    accent: false,
  },
  {
    id: "calendar-write",
    icon: CalendarDays,
    name: "It moves the meeting",
    scene: "\"Push my 3pm to Thursday.\"",
    line: "Not a suggestion you then go and do yourself. Rammy creates, moves and cancels real events in your Google Calendar — and leaves you an hour to undo anything it touched.",
    accent: true,
  },
  {
    id: "the-cast",
    icon: Users,
    name: "The same face, every picture",
    scene: "\"Draw Tango on a mossy log.\"",
    line: "Save a person, a pet or a product to your Cast once. Then just use their name in any prompt and they come back recognisably themselves — a whole set, not nine strangers.",
    accent: false,
  },
  {
    id: "studio",
    icon: Volume2,
    name: "Studio",
    scene: "A script at 11pm. A finished episode by 11:20.",
    line: "Narration in your chosen voices, a music bed that ducks under the words and swells in the pauses, exact pauses, chapter marks, and a captioned audiogram for 9:16. Change one word and only that paragraph re-voices.",
    accent: true,
  },
  {
    id: "images-no-picker",
    icon: ImageIcon,
    name: "No model picker, ever",
    scene: "\"A poster that says SOLD OUT.\"",
    line: "You should not have to learn five model names to get one picture. Rammy reads the prompt and routes it — readable type here, a real photograph there, your reference photo somewhere else. You just describe it.",
    accent: false,
  },
  {
    id: "creations",
    icon: FileText,
    name: "Nothing dies with the tab",
    scene: "The render from last Tuesday, still there.",
    line: "Every image and every audio render is kept in your Creations — play it, download it, or reopen the project and change one line.",
    accent: false,
  },
  {
    id: "rings",
    icon: BookOpen,
    name: "A memory you can read",
    scene: "\"Actually, she prefers oat milk.\"",
    line: "Rammy notices what matters and files it in your Rings — and every single item is visible, editable and deletable by you. Not a black box, and not training data.",
    accent: false,
  },
  {
    id: "rammy-anywhere",
    icon: Signal,
    name: "Rammy Anywhere",
    scene: "Your briefing arrives on Telegram.",
    line: "Reminders and briefings land in the thread you already check — not one more inbox to remember.",
    accent: false,
  },
  {
    id: "never-metered",
    icon: Heart,
    name: "Never metered on a free account",
    scene: "Chat as long as you want to.",
    line: "On a free account, chat, reminders and memory never cost you a credit — we only meter the expensive things, like a Studio render or a live voice minute. The guest preview on this page is 25 messages; after that it's a 20-second free account. A companion that counts your messages isn't a companion.",
    accent: true,
  },
];

function SectionHeroFeatures() {
  return (
    <section id="hero-features" className="scroll-mt-24 py-20 md:py-24 border-t border-[var(--rammy-border)]/50 bg-[var(--rammy-surface-alt)]/40" data-testid="section-hero-features">
      <div className="max-w-6xl mx-auto px-6">
        <div className="max-w-2xl">
          <Kicker testid="kicker-hero-features">What a chat window can't do</Kicker>
          <H2 className="mt-3">
            Nine things that only work when an AI{" "}
            <span className="text-[var(--rammy-cyan-dark)]">knows your life.</span>
          </H2>
          <Lead className="mt-5">
            Not one of these is a roadmap tile. Every card below is something you can do
            in Rammy today — because a promise on a homepage is still a promise.
          </Lead>
        </div>

        <div className="mt-12 grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          {HERO_FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.id}
                className={
                  f.accent
                    ? "rounded-3xl bg-[var(--rm-ink-900)] text-white p-7 flex flex-col rammy-shadow-lg"
                    : "rounded-3xl bg-[var(--rm-surface)] border border-black/[0.06] p-7 flex flex-col shadow-[0_8px_30px_rgba(17,19,24,0.03)] transition hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(17,19,24,0.06)]"
                }
                data-testid={`hero-feature-${f.id}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <span className={
                    f.accent
                      ? "w-11 h-11 rounded-2xl bg-white/10 flex items-center justify-center text-[var(--rammy-cyan)]"
                      : "w-11 h-11 rounded-2xl bg-[var(--rammy-cyan)]/15 flex items-center justify-center text-[var(--rammy-cyan-dark)]"
                  }>
                    <Icon size={18} strokeWidth={1.8}/>
                  </span>
                  <span className={
                    f.accent
                      ? "text-[10px] uppercase tracking-widest text-white/50 font-semibold pt-1.5"
                      : "text-[10px] uppercase tracking-widest text-[var(--rammy-cyan-dark)] font-semibold pt-1.5"
                  }>
                    Live today
                  </span>
                </div>
                <div className={f.accent ? "mt-5 font-display text-xl leading-snug text-white" : "mt-5 font-display text-xl leading-snug"}>
                  {f.name}
                </div>
                <div className={
                  f.accent
                    ? "mt-3 text-[15px] italic text-white/80 leading-relaxed"
                    : "mt-3 text-[15px] italic text-[var(--rammy-cyan-dark)] leading-relaxed"
                }>
                  {f.scene}
                </div>
                <div className={
                  f.accent
                    ? "mt-3 text-[15px] leading-relaxed text-white/70"
                    : "mt-3 text-[15px] leading-relaxed text-[var(--rammy-text-sec)]"
                }>
                  {f.line}
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-14 text-center">
          <Lead className="max-w-xl mx-auto">
            Rammy keeps your promises, books your time and makes the thing you needed —
            <span className="text-[var(--rammy-text)] font-medium"> so you stop being the one who has to remember everything.</span>
          </Lead>
          <Link to="/signup" className="inline-block mt-6" data-testid="hero-features-cta">
            <Button className="btn-pill btn-primary h-12 px-6 text-base">Try Rammy free <ArrowRight size={16} className="ml-1.5"/></Button>
          </Link>
        </div>
      </div>
    </section>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// Section: "Why not just ChatGPT?" — the silent objection every visitor
// arrives with. The page used to never answer it, which meant the visitor
// answered it themselves and left. Framed as a difference in *kind*, not a
// feature scoreboard we'd lose: a chat window answers, Rammy acts.
// Every right-hand claim must be true today (same honesty rule as the feature
// grid above) — guarded by backend/tests/test_landing_copy_honesty.py.
// ─────────────────────────────────────────────────────────────────────────────
const COMPARISON = [
  {
    id: "memory",
    q: "Does it remember me?",
    them: "A new conversation starts from nothing. What it does keep, you can't read or correct.",
    us: "Rammy files what matters into your Rings — and you can read every line, fix a wrong one, or delete it.",
  },
  {
    id: "acts",
    q: "Can it actually do the thing?",
    them: "It writes you the email. You still open your inbox and send it yourself.",
    us: "Rammy drafts it in Gmail, books the slot in your calendar, and moves the meeting — with an hour to undo.",
  },
  {
    id: "promises",
    q: "Does it carry it for me?",
    them: "You said you'd send it tomorrow. The chat forgot the moment you closed it.",
    us: "Rammy spots the promise, asks once, then carries it until it's kept — in both directions.",
  },
  {
    id: "voice",
    q: "Can I just talk to it?",
    them: "Push to talk, wait, read. It stops when the room gets noisy.",
    us: "Rammy starts speaking while the answer is still forming, waits until you're truly done, and ignores wind.",
  },
  {
    id: "honesty",
    q: "Will it make things up?",
    them: "Confident prose, invented numbers, no way to tell which is which.",
    us: "If Rammy isn't sure it says so, checks live, and shows you where the answer came from.",
  },
  {
    id: "cost",
    q: "Am I on a clock?",
    them: "A message cap that turns a conversation into a budget.",
    us: "Chat, reminders and memory are never metered on a free account — no message cap. The guest preview on this page is 25 messages, and the count is right there in the chat. Only the expensive renders and live voice minutes cost credits.",
  },
];

function SectionVsChat() {
  return (
    <section
      id="why-not-chatgpt"
      className="section scroll-mt-24 py-20 md:py-24 border-t border-[var(--rammy-border)]/50"
      data-testid="section-vs-chat"
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <Kicker testid="kicker-vs-chat">The honest question</Kicker>
          <H2 className="mt-3">
            "Why not just use{" "}
            <span className="text-[var(--rammy-cyan-dark)]">ChatGPT?"</span>
          </H2>
          <Lead className="mt-5">
            Fair. It's brilliant at answering. Rammy isn't trying to out-answer it —
            Rammy is the one that knows your week, holds what you said, and then goes
            and does it. Different job.
          </Lead>
        </div>

        <div className="mt-12 space-y-3" data-testid="comparison-rows">
          {/* Column headers — hidden on mobile, where each row stacks. */}
          <div className="hidden md:grid md:grid-cols-12 gap-4 px-6 pb-1">
            <div className="md:col-span-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--rammy-text-muted)]">
              What you're asking
            </div>
            <div className="md:col-span-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--rammy-text-muted)]">
              A chat window
            </div>
            <div className="md:col-span-4 text-[11px] uppercase tracking-[0.2em] font-semibold text-[var(--rammy-cyan-dark)]">
              Rammy
            </div>
          </div>

          {COMPARISON.map((row) => (
            <div
              key={row.id}
              data-testid={`comparison-row-${row.id}`}
              className="grid md:grid-cols-12 gap-4 md:gap-4 rounded-3xl bg-[var(--rm-surface)] border border-black/[0.06] p-6 md:px-6 md:py-5
                shadow-[0_8px_30px_rgba(17,19,24,0.03)] transition-[transform,box-shadow] duration-300
                hover:-translate-y-0.5 hover:shadow-[0_12px_40px_rgba(17,19,24,0.06)]"
            >
              <div className="md:col-span-4 font-display text-lg leading-snug text-[var(--rammy-text)]">
                {row.q}
              </div>
              <div className="md:col-span-4 text-[15px] leading-relaxed text-[var(--rammy-text-muted)]">
                <span className="md:hidden block text-[10px] uppercase tracking-[0.2em] font-semibold mb-1">A chat window</span>
                {row.them}
              </div>
              <div className="md:col-span-4 text-[15px] leading-relaxed text-[var(--rammy-text)]">
                <span className="md:hidden block text-[10px] uppercase tracking-[0.2em] font-semibold text-[var(--rammy-cyan-dark)] mb-1">Rammy</span>
                <span className="inline-flex gap-2">
                  <Check size={16} className="mt-1 text-[var(--rammy-cyan-dark)] shrink-0"/>
                  <span>{row.us}</span>
                </span>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-10 text-center">
          <p className="text-sm text-[var(--rammy-text-sec)] max-w-xl mx-auto leading-relaxed">
            Keep using ChatGPT for the hard questions. Use Rammy for your actual life.
          </p>
          <Link to="/signup" className="inline-block mt-6" data-testid="vs-chat-cta">
            <Button className="btn-pill btn-primary h-12 px-6 text-base">
              Start free — no card <ArrowRight size={16} className="ml-1.5"/>
            </Button>
          </Link>
        </div>
      </div>
    </section>
  );
}


// Section 1 — the voice difference (consolidated).
// Replaces the previous split between SectionDifference + SectionConversations —
// they told the same voice story twice. This section now owns the "voice"
// anchor and merges the 4 strongest cards from both. Memory + honesty each
// live in their own dedicated section below, so we don't tease them here.
function SectionDifference() {
  return (
    <section id="voice" className="section scroll-mt-24 pt-20 pb-16 md:pt-24 md:pb-20">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-3xl">
          <Kicker>The difference</Kicker>
          <H2 className="mt-4">Not another chatbot.<br/>A companion you can actually talk to.</H2>
          <Lead className="mt-5">
            Rammy listens like a companion and speaks while thinking — so replies feel fast,
            natural, and alive. No wake words. No "sorry, I didn't catch that."
          </Lead>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard icon={Mic} title="Speak naturally" testid="diff-speak">
            Tap the mic and talk. Rammy waits until you're actually done and then replies out loud —
            like a real conversation, not a walkie-talkie.
          </FeatureCard>
          <FeatureCard icon={Zap} title="Fast spoken replies" testid="diff-fast">
            Rammy starts speaking as the answer forms — no long silences while it "types".
          </FeatureCard>
          <FeatureCard icon={Volume2} title="Warm, expressive voices" testid="diff-voice">
            Six voices designed to feel calm, human, and alive. Preview each one before you pick.
          </FeatureCard>
          <FeatureCard icon={Waves} title="Reactive face" testid="diff-face">
            Warm rings pulse while you talk and while Rammy speaks — you can feel the conversation.
          </FeatureCard>
        </div>
      </div>
    </section>
  );
}

// Section 3 — day timeline
const TIMELINE = [
  { icon: Sunrise, kicker: "Morning clarity",   text: "“Good morning. You have two important things today. Want me to help you plan the first hour?”" },
  { icon: Zap,     kicker: "Focus reset",       text: "“Want a 25-minute focus block? I'll keep the goal simple.”" },
  { icon: ImageIcon, kicker: "Visual help",     text: "“Upload the screenshot. I'll tell you what's unclear.”" },
  { icon: Search,  kicker: "Fact check",        text: "“I'm not fully sure. Want me to check that live?”" },
  { icon: Moon,    kicker: "Evening reflection",text: "“Want me to save what mattered today?”" },
];
function SectionDay() {
  return (
    <section className="section scroll-mt-24 py-16 md:py-20 relative">
      <div className="max-w-5xl mx-auto px-6 md:px-10">
        <Kicker>A day, together</Kicker>
        <H2 className="mt-4 max-w-2xl">Small moments. Real help.<br/>Throughout your day.</H2>
        <div className="mt-12 relative">
          <div className="absolute left-6 top-0 bottom-0 w-px bg-[var(--rammy-border)]" aria-hidden="true"/>
          <ul className="space-y-8">
            {TIMELINE.map((t, i) => (
              <li key={i} className="relative pl-16" data-testid={`day-${i}`}>
                <div className="absolute left-0 top-0 w-12 h-12 rounded-full bg-[var(--rm-surface)] border border-[var(--rammy-border)] shadow-sm flex items-center justify-center text-[var(--rammy-cyan-dark)]">
                  <t.icon size={18} strokeWidth={1.6}/>
                </div>
                <div className="text-[11px] uppercase tracking-[0.22em] font-semibold text-[var(--rammy-text-muted)]">{t.kicker}</div>
                <p className="mt-2 font-serif text-2xl leading-relaxed text-[var(--rammy-text)]">{t.text}</p>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </section>
  );
}

// Section 4 (removed — consolidated into SectionDifference / "voice"). The
// old SectionConversations retold the exact same "not-a-chatbot / hands-free
// voice / fast replies / personalities" story with 4 more cards. We now own
// that story once, in SectionDifference above, so the reader isn't asked
// to read the same idea twice.

// Section 5 — Memory
function SectionMemory() {
  return (
    <section id="memory" className="section scroll-mt-24 py-16 md:py-20 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <Kicker>Memory that grows with you</Kicker>
          <H2 className="mt-4">A memory you can<br/>see, control, and grow.</H2>
          <Lead className="mt-5">
            Rammy remembers meaningful things you share and files them into your
            Rings. Rediscover moments. Edit anything. Delete anything.
          </Lead>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-12 gap-6">
          <Card className="md:col-span-7 md:row-span-2 flex flex-col justify-between min-h-[280px]" testid="mem-garden">
            <div>
              <div className="w-11 h-11 rounded-2xl bg-[var(--rammy-cyan)]/15 flex items-center justify-center text-[var(--rammy-cyan-dark)] mb-5">
                <Sparkles size={18} strokeWidth={1.8}/>
              </div>
              <h3 className="font-display text-2xl leading-snug mb-2">Your Rings</h3>
              <p className="text-[15px] leading-relaxed text-[var(--rammy-text-sec)] max-w-md">
                Browse everything Rammy remembers about you in a warm visual layout —
                goals, people, places, small preferences that make your days smoother.
              </p>
            </div>
            <div className="mt-6 flex flex-wrap gap-2">
              {["Prefers oat milk", "Runs on Tuesday nights", "Sister's birthday: 12 Aug",
                "Working on the redesign", "Loves Studio Ghibli", "Anxiety before flights"].map((m, i) => (
                <span key={i}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-[var(--rammy-cyan)]/10 border border-[var(--rammy-cyan)]/30 text-[13px] text-[var(--rammy-text)]">
                  <BookOpen size={11} className="text-[var(--rammy-cyan-dark)]"/>{m}
                </span>
              ))}
            </div>
          </Card>
          <FeatureCard icon={BookOpen} title="Automatic memory" className="md:col-span-5" testid="mem-auto">
            Rammy notices useful facts, goals, and preferences during your conversations —
            without you having to say "remember this."
          </FeatureCard>
          <FeatureCard icon={Heart}    title="On this day" className="md:col-span-5" testid="mem-onthisday">
            A month ago, six months ago, a year ago — Rammy quietly resurfaces the moments
            worth remembering.
          </FeatureCard>
          <FeatureCard icon={Lock}     title="You control it" className="md:col-span-6" testid="mem-control">
            Edit or delete any memory anytime. Your Rings are yours — not training data.
          </FeatureCard>
          <FeatureCard icon={ImageIcon} title="Image memories too" className="md:col-span-6" testid="mem-images">
            Photos, screenshots, and moments you share get thumbnailed and saved with the
            context — searchable later.
          </FeatureCard>
        </div>
      </div>
    </section>
  );
}

// Section 6 (removed — folded into SectionSafe below). The old SectionHonesty
// told the "no fake confidence · live checking · clear sources" story a
// third time (Difference had a Memory + Honest card; Safe already had an
// Honest card). SectionSafe now owns honesty end-to-end with a "Live
// checking" card that includes the sources idea.

// Section 7 — Multimodal input
function SectionUpload() {
  const uses = [
    "Review screenshots", "Understand product photos", "Discuss visual ideas",
    "Save image memories", "Ask questions about what you see",
  ];
  return (
    <section className="section scroll-mt-24 py-16 md:py-20 bg-[var(--rammy-surface-alt)]/40">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-6">
            <Kicker>Vision built in</Kicker>
            <H2 className="mt-4">Talk, type, upload, or paste.</H2>
            <Lead className="mt-5">
              Drop an image into the chat and Rammy can see it. Photos, screenshots,
              product shots, hand-drawn ideas — all of it becomes part of the conversation.
            </Lead>
          </div>
          <div className="md:col-span-6">
            <div className="rounded-3xl border border-dashed border-[var(--rammy-cyan-dark)]/40 bg-[var(--rm-surface)]/60 backdrop-blur-sm p-10 text-center">
              <div className="w-14 h-14 rounded-2xl bg-[var(--rammy-cyan)]/15 mx-auto flex items-center justify-center text-[var(--rammy-cyan-dark)]">
                <ImageIcon size={22} strokeWidth={1.6}/>
              </div>
              <p className="mt-5 font-display text-2xl leading-snug">Drop anything you can see.</p>
              <p className="mt-2 text-sm text-[var(--rammy-text-sec)]">JPG, PNG, screenshots. Paste from clipboard. Drag from your desktop.</p>
              <div className="mt-6 flex flex-wrap gap-2 justify-center">
                {uses.map((u, i) => (
                  <span key={i} className="px-3 py-1.5 rounded-full bg-[var(--rm-surface)] border border-[var(--rammy-border)] text-[13px] text-[var(--rammy-text-sec)]">{u}</span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Section 8 (removed 20 Jun 2026 — "Rammy Moments"). It sold a poster + audio
// clip of a voice exchange, with its own CTA, and no such feature exists
// anywhere in the backend. Do not rebuild the section; build the feature
// first if we want it.

// Section 9 — Multilingual
const LANGUAGES = ["English", "Hindi", "Punjabi", "Spanish", "Arabic", "French", "German", "Japanese", "Chinese", "Portuguese", "Italian", "Korean"];
function SectionLanguages() {
  return (
    <section className="section scroll-mt-24 py-16 md:py-20 bg-[var(--rm-ink-900)] text-[#faf7f2] overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 md:px-10 text-center">
        <Kicker>Multilingual</Kicker>
        <h2 className="mt-4 font-display text-4xl sm:text-5xl leading-[1.05] tracking-tight text-[#faf7f2]">
          Speak in the language that feels natural.
        </h2>
        <p className="mt-5 text-lg text-white/70 max-w-2xl mx-auto leading-relaxed">
          Rammy can detect your spoken language automatically. Switch mid-sentence, or slip
          between two — Rammy keeps up.
        </p>
      </div>
      <div className="mt-12 overflow-hidden">
        <div className="flex gap-10 animate-[marquee_40s_linear_infinite] whitespace-nowrap">
          {[...LANGUAGES, ...LANGUAGES, ...LANGUAGES].map((l, i) => (
            <span key={i} className="font-display text-4xl sm:text-5xl text-white/25 hover:text-[var(--rammy-cyan)] transition-colors">
              {l} <span className="text-white/10 mx-4">·</span>
            </span>
          ))}
        </div>
      </div>
    </section>
  );
}

// Section 10 — Privacy + Honesty (consolidated).
// SectionSafe now owns the full "trust" story: memory control, honesty
// (checks before guessing + shows sources), privacy, and sound control.
// This replaced the old separate SectionHonesty that told the same story
// with 3 more cards on the same page.
function SectionSafe() {
  return (
    <section className="section scroll-mt-24 py-16 md:py-20">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="max-w-2xl">
          <Kicker>Yours, always</Kicker>
          <H2 className="mt-4">Your thoughts should feel safe here.</H2>
          <Lead className="mt-5">
            Rammy is personal. That means control matters — over what's remembered, what's
            spoken back, and who can see any of it.
          </Lead>
        </div>
        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          <FeatureCard icon={BookOpen}    title="Control your memory" testid="safe-mem">
            View, edit, or delete anything Rammy remembers, one memory at a time.
            Your Rings are yours — not training data.
          </FeatureCard>
          <FeatureCard icon={Search}      title="Checks before guessing" testid="safe-honest">
            If Rammy isn't sure, it says so — then looks it up live and brings back a verified
            answer with the source next to it. No made-up numbers, no fake confidence.
          </FeatureCard>
          <FeatureCard icon={Lock}        title="Private by default" testid="safe-private">
            Your conversations and memories are personal. You choose what to share, always.
            No ads. No tracking.
          </FeatureCard>
          <FeatureCard icon={Volume2}     title="Sound control" testid="safe-sound">
            Mute the hero preview, switch voices, or turn off spoken replies — anytime.
          </FeatureCard>
        </div>
      </div>
    </section>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Pricing. The plans are SERVED, never hard-coded: this section used to
// hard-code Plus at $9 while the backend charged $6, and hid the Family tier
// entirely — the page even fetched /catalog/plans and threw the result away.
// Source of truth is backend/pricing.py.
//
// Tiers differ ONLY by the monthly credit allowance (agreed 20 Jun 2026).
// No feature gating: gating a voice is arbitrary, gating credits explains
// itself. Guarded by backend/tests/test_pricing_catalogue.py.
// ─────────────────────────────────────────────────────────────────────────────
const PLAN_HREF = { free: "/signup", plus: "/signup?plan=plus", family: "/signup?plan=family", pro: "/signup?plan=pro" };

/* "1167 minutes" reads like a spreadsheet. Past two hours, say hours. */
const voiceTime = (credits, perMinute = 3) => {
  const mins = Math.round(credits / perMinute);
  if (mins < 120) return `${mins} minutes`;
  return `${(mins / 60).toFixed(mins % 60 === 0 ? 0 : 1)} hours`;
};

function SectionPricing({ plans }) {
  const [annual, setAnnual] = useState(false);
  if (!plans?.length) return null;

  const monthsFree = (plans.find(p => p.annual_months_charged)?.annual_months_charged ?? 10);
  const freeMonths = 12 - monthsFree;

  return (
    <section id="pricing" className="section scroll-mt-24 py-16 md:py-20 bg-[var(--rammy-surface-alt)]/40" data-testid="section-pricing">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <Kicker>Plans</Kicker>
          <H2 className="mt-4">Every plan includes everything.<br/>You only choose how much you make.</H2>
          <Lead className="mt-5">
            No feature is locked behind a higher tier. Chat, reminders and memory are never
            metered — plans differ only in the credits you get for the expensive things:
            Studio renders, images, and live voice minutes.
          </Lead>

          {/* Monthly / annual toggle */}
          <div className="mt-8 inline-flex items-center gap-1 p-1 rounded-full bg-[var(--rm-surface)] border border-[var(--rammy-border)]" data-testid="billing-toggle">
            {[
              { key: false, label: "Monthly", testid: "billing-monthly" },
              { key: true,  label: `Annual · ${freeMonths} months free`, testid: "billing-annual" },
            ].map(opt => (
              <button
                key={String(opt.key)}
                onClick={() => setAnnual(opt.key)}
                data-testid={opt.testid}
                aria-pressed={annual === opt.key}
                className={`px-4 py-2 rounded-full text-sm transition-colors ${
                  annual === opt.key
                    ? "bg-[var(--rm-ink-900)] text-white"
                    : "text-[var(--rammy-text-sec)] hover:text-[var(--rammy-text)]"
                }`}
              >
                {opt.label}
              </button>
            ))}
          </div>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 items-start">
          {plans.map(p => {
            const paid = p.price_usd > 0;
            const shown = annual && paid ? p.price_usd_annual : p.price_usd;
            return (
              <div key={p.id} data-testid={`plan-${p.id}`}
                className={`rounded-3xl bg-[var(--rm-surface)] p-7 border transition-all duration-300 flex flex-col h-full
                  ${p.highlighted
                    ? "border-[var(--rammy-cyan)] shadow-[0_20px_60px_rgba(72,224,200,0.20)] lg:-translate-y-4"
                    : "border-black/[0.06] shadow-[0_8px_30px_rgba(17,19,24,0.03)]"}`}>
                {p.highlighted && (
                  <span className="self-start mb-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--rammy-cyan)]/20 text-[var(--rammy-cyan-dark)] text-[11px] uppercase tracking-widest font-semibold">
                    <Star size={11}/> Most loved
                  </span>
                )}
                <div className="font-display text-2xl">{p.name}</div>
                <div className="mt-3 flex items-baseline gap-1">
                  <span className="font-display text-4xl leading-none" data-testid={`plan-${p.id}-price`}>${shown}</span>
                  {paid && (
                    <span className="text-[var(--rammy-text-muted)] text-sm">{annual ? "/yr" : "/mo"}</span>
                  )}
                </div>

                {/* The one number that actually separates the tiers. */}
                <div className="mt-4 rounded-2xl bg-[var(--rammy-cyan)]/10 border border-[var(--rammy-cyan)]/30 px-3.5 py-2.5">
                  <div className="font-medium text-[15px] text-[var(--rammy-text)]" data-testid={`plan-${p.id}-credits`}>
                    {p.credits.toLocaleString()} credits / month
                  </div>
                  <div className="text-[12px] text-[var(--rammy-text-sec)] mt-0.5">
                    ≈ {voiceTime(p.credits)} of live voice
                    {p.seats > 1 && ` · ${p.seats} seats`}
                  </div>
                </div>

                <p className="mt-4 text-sm text-[var(--rammy-text-sec)] leading-relaxed">{p.tagline}</p>
                <ul className="mt-5 space-y-2 text-[14px] flex-1">
                  {p.features.map((f, i) => (
                    <li key={i} className="flex items-start gap-2.5">
                      <Check size={15} className="mt-0.5 text-[var(--rammy-cyan-dark)] shrink-0"/>
                      <span className="text-[var(--rammy-text-sec)]">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link to={PLAN_HREF[p.id] || "/signup"} data-testid={`plan-${p.id}-cta`} className="mt-7">
                  <Button className={`btn-pill w-full h-11 ${p.highlighted ? "btn-primary" : "bg-[var(--rm-surface)] border border-[var(--rammy-text)] text-[var(--rammy-text)] hover:bg-[var(--rm-ink-900)] hover:text-white"}`}>
                    {p.cta}
                  </Button>
                </Link>
              </div>
            );
          })}
        </div>

        <p className="mt-8 text-center text-sm text-[var(--rammy-text-sec)]">
          No card to try. Cancel any time. Run out of credits and you can top up —
          you're never cut off mid-sentence.
        </p>
      </div>
    </section>
  );
}

/* ---------- Page ---------- */
export default function Landing() {
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  useEffect(() => { api.get("/catalog/plans").then(r => setPlans(r.data)).catch(() => {}); }, []);

  return (
    <div className="grain min-h-screen" style={{ background: "var(--rm-bg)" }}>
      <Nav authed={!!user} />

      {/* HERO — the actual live product surface, framed as such */}
      <div id="hero">
        <HeroChat />
      </div>

      {/* The "we know your life" story, then the objection every visitor
          already has, answered head-on before we ask for the signup. */}
      <SectionHeroFeatures />
      <SectionVsChat />
      <SectionConnectors />

      <SectionDifference />
      <SectionDay />
      <SectionMemory />
      <SectionUpload />
      <SectionLanguages />
      <SectionSafe />
      <SectionPricing plans={plans} />

      <Footer />
    </div>
  );
}
